import React from 'react';
import { Card, CardContent, Box, Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { EmojiEvents, Download, Share } from '@mui/icons-material';
import Typography from 'themeComponents/Typography';
import Chip from '@mui/material/Chip';

interface CertificateDisplayProps {
  certificateNumber: string;
  courseTitle: string;
  userName: string;
  issuedAt: string;
  pdfUrl?: string;
  imageUrl?: string;
  onDownload?: () => void;
  onShare?: () => void;
}

export const CertificateDisplay: React.FC<CertificateDisplayProps> = ({
  certificateNumber,
  courseTitle,
  userName,
  issuedAt,
  pdfUrl,
  imageUrl,
  onDownload,
  onShare,
}) => {
  const [openPreview, setOpenPreview] = React.useState(false);
  const issueDateFormatted = new Date(issuedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <>
      <Card
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&:hover': { boxShadow: 10, transform: 'translateY(-4px)' },
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="start" justifyContent="space-between" gap={2}>
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <EmojiEvents sx={{ fontSize: 28 }} />
                <Typography variant="h5" fontWeight="bold">
                  Certificate of Completion
                </Typography>
              </Box>

              <Typography variant="body2" opacity={0.9} mb={2}>
                This certifies that
              </Typography>

              <Typography variant="h6" fontWeight="bold" mb={1}>
                {userName}
              </Typography>

              <Typography variant="body2" opacity={0.9} mb={2}>
                has successfully completed the course
              </Typography>

              <Typography variant="h6" fontWeight="bold" sx={{ fontStyle: 'italic' }} mb={2}>
                {courseTitle}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" opacity={0.8} display="block">
                    Certificate Number
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {certificateNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" opacity={0.8} display="block">
                    Issued On
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {issueDateFormatted}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box display="flex" gap={1} mt={3} flexWrap="wrap">
            {pdfUrl && (
              <Button
                variant="contained"
                color="inherit"
                size="small"
                startIcon={<Download />}
                onClick={onDownload}
                sx={{
                  color: '#667eea',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                }}
              >
                Download PDF
              </Button>
            )}
            {imageUrl && (
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => setOpenPreview(true)}
                sx={{
                  borderColor: 'white',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Preview
              </Button>
            )}
            {onShare && (
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<Share />}
                onClick={onShare}
                sx={{
                  borderColor: 'white',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Share
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {imageUrl && (
        <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="md" fullWidth>
          <DialogTitle>Certificate Preview</DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Box component="img" src={imageUrl} alt="Certificate" sx={{ maxWidth: '100%', height: 'auto' }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPreview(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
